import { useState, useEffect } from 'react';
import api from '../../services/api';

interface GamificationSummary {
  level: {
    current: number;
    title: string;
    icon: string;
    color: string;
    currentXP: number;
    totalXP: number;
    xpToNextLevel: number;
    progressPercentage: number;
  };
  streak: {
    current: number;
    longest: number;
    lastStudyDate: string | null;
    totalDays: number;
  };
  badges: {
    earned: number;
    total: number;
    recent: Array<{
      type: string;
      name: string;
      icon: string;
      color: string;
      earnedAt: string;
    }>;
  };
  leaderboard: {
    userRank: number | null;
    topEntries: Array<{
      rank: number;
      userId: number;
      level: number;
      levelTitle: string;
      totalXP: number;
      levelIcon: string;
    }>;
  };
}

export default function GamificationPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<GamificationSummary | null>(null);

  useEffect(() => {
    fetchGamificationData();
  }, []);

  const fetchGamificationData = async () => {
    try {
      setLoading(true);
      const summaryRes = await api.get('/gamification/summary');
      setSummary(summaryRes.data.data);
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStreakMessage = () => {
    if (!summary) return '';
    const streak = summary.streak.current;
    
    if (streak === 0) return "Start your journey today! 🚀";
    if (streak === 1) return "Great start! Keep it going! 💪";
    if (streak < 7) return `You're on a ${streak}-day streak — don't break it today! 🔥`;
    if (streak < 30) return `Amazing ${streak}-day streak! You're unstoppable! ⚡`;
    return `Legendary ${streak}-day streak! You're a champion! 👑`;
  };

  const getNextMilestone = () => {
    if (!summary) return null;
    const streak = summary.streak.current;
    const milestones = [3, 7, 14, 30, 60, 90, 180, 365];
    return milestones.find(m => m > streak) || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load gamification data. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Progress</h1>
        <p className="text-gray-600 mt-2">{getStreakMessage()}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium opacity-90">Current Streak</span>
            <span className="text-3xl">🔥</span>
          </div>
          <div className="text-4xl font-bold">{summary.streak.current}</div>
          <div className="text-sm opacity-90 mt-1">days</div>
          {getNextMilestone() && (
            <div className="mt-3 text-xs opacity-75">
              Next milestone: {getNextMilestone()} days
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium opacity-90">Level</span>
            <span className="text-3xl">{summary.level.icon}</span>
          </div>
          <div className="text-4xl font-bold">{summary.level.current}</div>
          <div className="text-sm opacity-90 mt-1">{summary.level.title}</div>
          <div className="mt-3">
            <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2 transition-all duration-500"
                style={{ width: `${summary.level.progressPercentage}%` }}
              ></div>
            </div>
            <div className="text-xs opacity-75 mt-1">
              {summary.level.currentXP} / {summary.level.xpToNextLevel} XP
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium opacity-90">Achievements</span>
            <span className="text-3xl">🏆</span>
          </div>
          <div className="text-4xl font-bold">{summary.badges.earned}</div>
          <div className="text-sm opacity-90 mt-1">of {summary.badges.total} unlocked</div>
          <div className="mt-3 text-xs opacity-75">
            {Math.round((summary.badges.earned / summary.badges.total) * 100)}% complete
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-teal-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium opacity-90">Total XP</span>
            <span className="text-3xl">⭐</span>
          </div>
          <div className="text-4xl font-bold">{summary.level.totalXP.toLocaleString()}</div>
          <div className="text-sm opacity-90 mt-1">experience points</div>
          <div className="mt-3 text-xs opacity-75">
            Longest streak: {summary.streak.longest} days
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-8 text-white text-center">
        <div className="text-2xl font-bold mb-3">
          "Success is the sum of small efforts repeated day in and day out." 💪
        </div>
        <p className="text-lg opacity-90">
          You're {summary.streak.current > 0 ? 'doing amazing' : 'ready to start'}! Keep pushing forward! 🚀
        </p>
      </div>
    </div>
  );
}
